import { useMemo, useRef } from "react";
import type { Addon, Item } from "../../../api/menu";
import { useMenuItems } from "../../../hooks/useMenu";
import { lunchCategoryId, lunchKeyOfItem, lunchPairKey, type LunchCounters } from "../../../lib/lunchCatalog";
import { lunchRoomOfKey } from "../../../lib/lunchPortions";
import { addonOptionIdsInCatalog, productAddonsOf } from "../../../lib/lunchAddonOptions";

interface HiddenLunchItemsOptions {
  allItems: Item[];
  allAddons: Addon[];
  counters: LunchCounters | undefined;
  cartUsage: Map<string, number>;
  // Бройката на копието в кошницата — с бройка то остава видимо („−" работи)
  quantityOf: (itemId: number) => number;
}

const NOTHING_HIDDEN = { hiddenItemIds: new Set<number>(), hiddenCategoryIds: new Set<number>() };

const sameSet = (first: ReadonlySet<number>, second: ReadonlySet<number>) =>
  first.size === second.size && [...first].every((id) => second.has(id));

/**
 * Задължителна добавка (тип 2) на копие от дневно меню, за която не стигат
 * опциите с порции: изисква `maxQty` различни (не повече от опциите в каталога,
 * както „Промени"), а с порции са останали по-малко — копието не може да се
 * поръча. `catalogIds` — продуктите, както ги показва „Промени" (`useMenuItems`).
 */
export const lunchRequiredAddonSoldOut = (
  item: Item,
  allAddons: Addon[],
  catalogIds: ReadonlySet<number>,
  counters: LunchCounters | undefined,
  usage: Map<string, number>,
) => {
  const lunchMenuId = item.lunchMenuId;
  if (lunchMenuId == null || !counters) return false;
  return productAddonsOf(item, allAddons).some((addon) => {
    if (addon.type !== 2) return false;
    const optionIds = addonOptionIdsInCatalog(addon, catalogIds);
    const required = Math.min(addon.maxQty || 1, optionIds.length);
    const withPortions = optionIds.filter(
      (optionId) => lunchRoomOfKey(counters, usage, lunchPairKey(lunchMenuId, optionId)) > 0,
    ).length;
    return withPortions < required;
  });
};

const computeHidden = (
  allItems: Item[],
  catalogIds: ReadonlySet<number>,
  allAddons: Addon[],
  counters: LunchCounters,
  cartUsage: Map<string, number>,
  quantityOf: (itemId: number) => number,
) => {
  const hiddenItemIds = new Set<number>();
  const hiddenCategoryIds = new Set<number>();
  const copiesByCategory = new Map<number, Item[]>();
  for (const item of allItems) {
    if (item.lunchMenuId == null) continue;
    const categoryId = lunchCategoryId(item.lunchMenuId);
    copiesByCategory.set(categoryId, [...(copiesByCategory.get(categoryId) ?? []), item]);
    if (quantityOf(item.id) > 0) continue;
    const soldOut = lunchRoomOfKey(counters, cartUsage, lunchKeyOfItem(item)) <= 0;
    if (soldOut || lunchRequiredAddonSoldOut(item, allAddons, catalogIds, counters, cartUsage))
      hiddenItemIds.add(item.id);
  }
  for (const [categoryId, copies] of copiesByCategory) {
    if (copies.every((copy) => hiddenItemIds.has(copy.id))) hiddenCategoryIds.add(categoryId);
  }
  return { hiddenItemIds, hiddenCategoryIds };
};

/**
 * Изчерпаното от дневните менюта се СКРИВА (не стои бледо): копие без порции
 * или със задължителна добавка, чиито опции са изчерпани — освен ако вече е в
 * кошницата. Група без видим продукт губи и чипа си. Копията остават в менюто
 * (`withLunchMenus`) — иначе кошницата би минала на основна цена
 * (`useEndedLunchCartItems`); скриването е само в изгледа.
 */
export const useHiddenLunchItems = ({
  allItems,
  allAddons,
  counters,
  cartUsage,
  quantityOf,
}: HiddenLunchItemsOptions) => {
  // Опциите — както ги брои „Промени" (useAddonSelection): и неактивните продукти
  const menuItems = useMenuItems();
  const catalogIds = useMemo(
    () => new Set(menuItems.filter((item) => item.lunchMenuId == null).map((item) => item.id)),
    [menuItems],
  );
  // Същото съдържание → същите обекти: каталогът връща фокуса в началото при
  // всяка нова референция на групите (ефектът в ItemCatalog), а кошницата се
  // сменя при всяко „+"
  const previous = useRef(NOTHING_HIDDEN);
  const hidden = useMemo(() => {
    if (!counters) return NOTHING_HIDDEN;
    const next = computeHidden(allItems, catalogIds, allAddons, counters, cartUsage, quantityOf);
    return sameSet(next.hiddenItemIds, previous.current.hiddenItemIds) &&
      sameSet(next.hiddenCategoryIds, previous.current.hiddenCategoryIds)
      ? previous.current
      : next;
  }, [allItems, catalogIds, allAddons, counters, cartUsage, quantityOf]);
  previous.current = hidden;
  return { ...hidden, catalogIds };
};

/** Опциите на добавките, които се скриват в „Промени" за ред от дневно меню. */
export const hiddenLunchAddonOptions = (
  item: Item | null | undefined,
  allAddons: Addon[],
  counters: LunchCounters | undefined,
  cartUsage: Map<string, number>,
): Set<number> => {
  const hidden = new Set<number>();
  if (!item || item.lunchMenuId == null || !counters) return hidden;
  for (const addon of productAddonsOf(item, allAddons)) {
    for (const option of addon.items) {
      if (lunchRoomOfKey(counters, cartUsage, lunchPairKey(item.lunchMenuId, option.itemId)) <= 0)
        hidden.add(option.itemId);
    }
  }
  return hidden;
};
